import api from './api';
import { getPrintServiceUrl } from './operatorService';

export interface StationWithDetails {
  station_id: number;
  station_name: string;
  station_type: string;
  location_code: string;
  location_name: string;
  warehouse_code: string;
  station_status: string;
  enabled: boolean;
  target_weight: number | null;
  description: string | null;
  allowed_material_type: string | null;
  session_number: string | null;
  sub_session_number: string | null;
  product_sku: string | null;
  product_name: string | null;
  product_chinese_name: string | null;
  unit_weight: number | null;
  raw_material_item_number: string | null;
  raw_material_name: string | null;
  session_status: string | null;
  assigned_operator: string | null;
  assigned_operator_name: string | null;
  station_operator_id: number | null;
  initial_material_weight: number | null;
  actual_output_weight: number | null;
  target_output_weight: number | null;
  started_at: string | null;
  session_created_at: string | null;
  queue_count: number;
  // Current operator and packer from station_activity_log
  current_operator_name: string | null;
  operator_employee_number: string | null;
  current_packer_name: string | null;
  packer_employee_number: string | null;
  // Box counts
  total_boxes: number;
  boxes_produced: number;
  // Printer
  printer_mac_id: string | null;
  // On-hold material flag
  has_pending_onhold: boolean;
  // Worker active status (false = on break)
  operator_is_active: boolean;
  packer_is_active: boolean;
}

export interface Operator {
  user_id: string;
  full_name: string;
  employee_number: string;
  disabled: string;
}

const stationManagementService = {
  // Get all stations with sessions and operators
  async getStations(): Promise<StationWithDetails[]> {
    const response = await api.get('/stations');
    return response.data.data.stations;
  },

  // Get available operators
  async getOperators(scheduledDate?: string): Promise<Operator[]> {
    const params = scheduledDate ? { scheduled_date: scheduledDate } : {};
    const response = await api.get('/stations/manage/operators', { params });
    return response.data.data.operators;
  },

  async getPackers(scheduledDate?: string): Promise<Operator[]> {
    const params = scheduledDate ? { scheduled_date: scheduledDate } : {};
    const response = await api.get('/stations/manage/packers', { params });
    return response.data.data.packers;
  },

  // Assign session to station
  async assignSession(stationId: number, sessionId: string) {
    const response = await api.post(
      `/stations/manage/${stationId}/assign-session`,
      { session_number: sessionId }
    );
    return response.data;
  },

  // Unplan/unassign session from station
  async unplanSession(stationId: number) {
    const response = await api.post(
      `/stations/manage/${stationId}/unplan-session`
    );
    return response.data;
  },

  // Assign operator to station's session
  async assignOperator(stationId: number, operatorId: string) {
    const response = await api.post(
      `/stations/manage/${stationId}/assign-operator`,
      { operator_id: operatorId }
    );
    return response.data;
  },

  // Update station material type restriction
  async updateMaterialType(stationId: number, materialType: string | null) {
    const response = await api.put(
      `/stations/manage/${stationId}/material-type`,
      { material_type: materialType }
    );
    return response.data;
  },

  // Assign operator to sub-session
  async assignSubSessionOperator(subSessionNumber: string, operatorId: string, supervisorName?: string) {
    const response = await api.post(
      `/stations/manage/sub-sessions/${subSessionNumber}/assign-operator`,
      { operator_id: operatorId, ...(supervisorName && { supervisor_name: supervisorName }) }
    );
    return response.data;
  },

  // Assign or unassign station from sub-session
  // Pass stationId = null to unassign (removes station, sets status to 'received')
  // Optional reason/reason_other for active/paused sub-sessions (mid-production changes)
  // confirmed: true to proceed when there's an active sub-session conflict (priority queue)
  async assignSubSessionStation(
    subSessionNumber: string,
    stationId: number | null,
    options?: { reason?: string; reason_other?: string; supervisor_id?: string; confirmed?: boolean }
  ) {
    const response = await api.post(
      `/stations/manage/sub-sessions/${subSessionNumber}/assign-station`,
      {
        station_id: stationId,
        ...(options?.reason && { reason: options.reason }),
        ...(options?.reason_other && { reason_other: options.reason_other }),
        ...(options?.supervisor_id && { supervisor_id: options.supervisor_id }),
        ...(options?.confirmed && { confirmed: options.confirmed })
      }
    );
    return response.data;
  },

  // Toggle station enabled/disabled
  async toggleStationEnabled(stationId: number, enabled: boolean) {
    const response = await api.put(
      `/stations/manage/${stationId}/toggle-enabled`,
      { enabled }
    );
    return response.data;
  },

  // Assign packer to sub-session
  async assignSubSessionPacker(subSessionNumber: string, packerId: string | null, supervisorId?: string, supervisorName?: string) {
    const response = await api.post(
      `/stations/manage/sub-sessions/${subSessionNumber}/assign-packer`,
      { packer_id: packerId, supervisor_id: supervisorId, ...(supervisorName && { supervisor_name: supervisorName }) }
    );
    return response.data;
  },

  // Update sub-session weight mode (fixed vs catch)
  async updateSubSessionWeightMode(subSessionNumber: string, weightMode: 'fixed' | 'catch' | 'scale') {
    const response = await api.put(
      `/stations/manage/sub-sessions/${subSessionNumber}/weight-mode`,
      { weight_mode: weightMode }
    );
    return response.data;
  },

  // Update sub-session initial material weight (only for planned/ready sub-sessions)
  async updateSubSessionWeight(subSessionNumber: string, weight: number) {
    const response = await api.put(
      `/stations/manage/sub-sessions/${subSessionNumber}/weight`,
      { initial_material_weight: weight }
    );
    return response.data;
  },

  // Cancel a planned/ready sub-session (returns material to lot)
  async cancelSubSession(subSessionNumber: string) {
    const response = await api.post(
      `/stations/manage/sub-sessions/${subSessionNumber}/cancel`
    );
    return response.data;
  },

  // Allocate a finish_product_ni lot to a sub-session (WCO-2116 carryover flow)
  async addFinishProductToSession(
    sessionId: string,
    subSessionNumber: string,
    lotId: string,
    reason: 'leftover_previous_session' | 'supervisor_decision',
    supervisorId?: string
  ) {
    const response = await api.post(
      `/stations/manage/sessions/${sessionId}/add-finish-product`,
      {
        sub_session_number: subSessionNumber,
        lot_id: lotId,
        reason,
        ...(supervisorId && { supervisor_id: supervisorId })
      }
    );
    return response.data;
  },

  // Add material to an existing session mid-production
  // Returns action: 'distributed' (same material) or 'created' (new sub-session)
  // target_sub_session_numbers: optional array of sub-session numbers to distribute material to
  // If provided, material is distributed only to those sub-sessions (proportionally)
  // If not provided and multiple sub-sessions use same material, distributes to all of them
  async addMaterialToSession(
    sessionId: string,
    itemNumber: string,
    transferNumber: string,
    weight: number,
    options: { reason: string; reason_other?: string; supervisor_id?: string; target_sub_session_numbers?: string[]; yield_factor?: number; lot_id?: string }
  ) {
    const response = await api.post(
      `/stations/manage/sessions/${sessionId}/add-material`,
      {
        item_number: itemNumber,
        transfer_number: transferNumber,
        weight,
        reason: options.reason,
        ...(options.reason_other && { reason_other: options.reason_other }),
        ...(options.supervisor_id && { supervisor_id: options.supervisor_id }),
        ...(options.target_sub_session_numbers && { target_sub_session_numbers: options.target_sub_session_numbers }),
        ...(options.yield_factor && { yield_factor: options.yield_factor }),
        // WCO-2607: when the modal picks a specific lot, forward its lot_id so the
        // backend consumes exactly that lot (transfer_number is derived server-side).
        ...(options.lot_id && { lot_id: options.lot_id })
      }
    );
    return response.data;
  },

  // ============================================
  // Printer Assignment
  // ============================================

  // Get all stations with their printer MACs
  async getStationPrinters(): Promise<Array<{ station_id: number; station_name: string; printer_mac_id: string | null; enabled: boolean; location_code?: string }>> {
    const response = await api.get('/stations/manage/printers');
    return response.data.data.stations;
  },

  // Update a station's printer MAC ID (config_settings key)
  async updateStationPrinter(stationId: number, printerMacId: string | null) {
    const response = await api.put(`/stations/manage/${stationId}/printer`, { printer_mac_id: printerMacId });
    return response.data;
  },

  // Test print to a station's assigned printer
  async testStationPrinter(stationId: number): Promise<{ success: boolean; message?: string; error?: string; printer?: { ip: string; port: number } }> {
    const base = getPrintServiceUrl();
    const url = base ? `${base}/api/print/test` : '/api/print/test';
    const response = await api.post(url, { station_id: stationId });
    return response.data;
  },

  // Discover printers on the network
  async discoverPrinters(subnet?: string): Promise<{ success: boolean; printers: Array<{ ip: string; mac: string; reachable: boolean }>; scanned: number }> {
    const base = getPrintServiceUrl();
    const url = base ? `${base}/api/print/discover` : '/api/print/discover';
    const params = subnet ? `?subnet=${subnet}` : '';
    const response = await api.get(`${url}${params}`);
    return response.data;
  },

  // Promote a planned sub-session to ready (supervisor "Next" action)
  // Skips scheduled_date check, demotes any existing ready at the station
  async promoteSubSessionNext(subSessionNumber: string) {
    const response = await api.post(
      `/stations/manage/sub-sessions/${subSessionNumber}/promote-next`
    );
    return response.data;
  },

  // Manually change a sub-session's status (e.g. ready → planned)
  async updateSubSessionStatus(subSessionNumber: string, status: string) {
    const response = await api.put(
      `/stations/manage/sub-sessions/${subSessionNumber}/status`,
      { status }
    );
    return response.data;
  },

  // Run auto-promotion on all enabled stations
  // Returns { promoted: [{ sub_session_number, session_number, station_name, operator_name, packer_name }] }
  async promoteAllStations() {
    const response = await api.post('/stations/manage/promote-all-stations');
    return response.data;
  },

  // Subdivide a sub-session into two - split remaining weight to a new sub-session
  async subdivideSubSession(
    subSessionNumber: string,
    splitWeight: number,
    options: {
      reason: string;
      reason_other?: string;
      target_station_id?: number;
      operator_id?: string;
      packer_id?: string;
      supervisor_id?: string;
    }
  ) {
    const response = await api.post(
      `/stations/manage/sub-sessions/${subSessionNumber}/subdivide`,
      {
        split_weight: splitWeight,
        reason: options.reason,
        ...(options.reason_other && { reason_other: options.reason_other }),
        ...(options.target_station_id && { target_station_id: options.target_station_id }),
        ...(options.operator_id && { operator_id: options.operator_id }),
        ...(options.packer_id && { packer_id: options.packer_id }),
        ...(options.supervisor_id && { supervisor_id: options.supervisor_id })
      }
    );
    return response.data;
  },
};

export default stationManagementService;
